<template>
  <div
    class="w-[190px] h-[128px] bg-white border border-[#dedede]/20 rounded-[6px] flex flex-col"
    :style="{
      boxShadow: '0 2px 10px rgba(0,0,0,0.10), 0 1px 20px rgba(0,0,0,0.05)',
    }"
    @pointerleave="emit('leave')"
  >
    <!-- Content stack: 86px tall, 6px inset all sides plus 4px inner pad -->
    <div class="flex-1 px-[10px] pt-[6px] flex flex-col">
      <!-- Header label row -->
      <div
        class="font-secondary text-[11px] leading-[14px] text-[#6e7480] uppercase tracking-[0.02em]"
      >
        Potential infringement {{ match.number }}
      </div>

      <!-- Title row: icon + title, mt 7px to land at y=21 within content -->
      <div class="mt-[7px] flex items-center gap-x-2">
        <Icon name="Alert" :size="12" class="shrink-0" :class="iconClass" />
        <div
          class="font-primary font-medium text-[13px] leading-[17px] text-black truncate"
        >
          {{ match.title }}
        </div>
      </div>

      <!-- Match row: mt to land at y=42 -->
      <div class="mt-[4px] flex items-center justify-between">
        <div class="font-secondary text-[12px] leading-[15px] text-[#6e7480]">
          Match
        </div>
        <div
          class="font-secondary text-[12px] leading-[15px] tabular-nums"
          :style="{ color: levelColor }"
        >
          {{ Math.round(match.score) }}%
        </div>
      </div>

      <!-- Location row: mt to land at y=65 -->
      <div
        v-if="location"
        class="mt-[4px] flex items-center justify-between gap-x-2"
      >
        <div class="font-secondary text-[12px] leading-[15px] text-[#6e7480]">
          Location
        </div>
        <div
          class="font-secondary text-[12px] leading-[15px] text-[#292c30] truncate"
        >
          {{ location }}
        </div>
      </div>
    </div>

    <div class="px-[6px] pb-[6px]">
      <Button size="md" class="w-full" @click="emit('view')">View</Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Icon } from '@dacr/ui'
import { Button } from '@dacr/ui/next'
import { ScoreLevel } from '@/shared/graphql/router/graphql'
import type { TimelineMatch } from './timeline-types'

const props = defineProps<{
  match: TimelineMatch
}>()

const emit = defineEmits<{
  view: []
  leave: []
}>()

const LEVEL_COLOR: Record<ScoreLevel, string> = {
  [ScoreLevel.High]: '#dc3c3f',
  [ScoreLevel.Medium]: '#f5b958',
  [ScoreLevel.Low]: '#34b1c7',
}

const LEVEL_STROKE: Record<ScoreLevel, string> = {
  [ScoreLevel.High]: 'stroke-[#dc3c3f]',
  [ScoreLevel.Medium]: 'stroke-[#f5b958]',
  [ScoreLevel.Low]: 'stroke-[#34b1c7]',
}

const levelColor = computed(
  () => LEVEL_COLOR[props.match.level] ?? LEVEL_COLOR[ScoreLevel.High]
)

const iconClass = computed(
  () => LEVEL_STROKE[props.match.level] ?? LEVEL_STROKE[ScoreLevel.High]
)

const location = computed(() => props.match.location?.trim() ?? '')
</script>
